import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './UI';

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    // Determine page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5; // Show 5 page numbers max (e.g., 1 ... 4 5 6 ... 10)
        const halfMax = Math.floor(maxPagesToShow / 2);

        if (totalPages <= maxPagesToShow + 2) { // Show all pages if not too many
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1); // Always show first page
            if (currentPage > halfMax + 2) {
                pages.push('...'); // Ellipsis after page 1
            }

            // Calculate window around current page
            let startPage = Math.max(2, currentPage - halfMax);
            let endPage = Math.min(totalPages - 1, currentPage + halfMax);

            // Adjust window if near the beginning
            if (currentPage <= halfMax + 1) {
                endPage = Math.min(totalPages - 1, maxPagesToShow);
            }
            // Adjust window if near the end
            if (currentPage >= totalPages - halfMax) {
                startPage = Math.max(2, totalPages - maxPagesToShow + 1);
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (currentPage < totalPages - halfMax - 1) {
                pages.push('...'); // Ellipsis before last page
            }
            pages.push(totalPages); // Always show last page
        }
        return pages;
    };

    const pageNumbers = getPageNumbers();

    if (totalPages <= 1) {
        return null; // Don't render pagination if only one page or less
    }

    return (
        <div className="flex items-center justify-center space-x-1 sm:space-x-2 mt-6 py-2"> {/* Reduced spacing */}
            {/* Previous Button */}
            <Button
                variant="secondary"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="disabled:opacity-50 disabled:cursor-not-allowed px-2 sm:px-3" // Adjusted padding
                aria-label="Previous Page"
            >
                <ChevronLeft className="w-4 h-4 sm:mr-1" /> {/* Hide text on small screens */}
                <span className="hidden sm:inline">Previous</span>
            </Button>

            {/* Page Numbers */}
            {pageNumbers.map((page, index) =>
                typeof page === 'number' ? (
                    <Button
                        key={page}
                        variant={currentPage === page ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => onPageChange(page)} // Use 1-based page number
                        className={`w-9 h-9 p-0 flex items-center justify-center ${currentPage === page ? 'font-bold ring-2 ring-primary-300 ring-offset-1' : 'hover:bg-gray-100'}`}
                        aria-label={`Page ${page}`}
                        aria-current={currentPage === page ? 'page' : undefined}
                    >
                        {page}
                    </Button>
                ) : (
                    <span key={`ellipsis-${index}`} className="text-neutral-500 px-1 sm:px-2 flex items-center h-9">
                        {page}
                    </span>
                )
            )}

            {/* Next Button */}
            <Button
                variant="secondary"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="disabled:opacity-50 disabled:cursor-not-allowed px-2 sm:px-3" // Adjusted padding
                aria-label="Next Page"
            >
                 <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-4 h-4 sm:ml-1" /> {/* Hide text on small screens */}
            </Button>
        </div>
    );
};

export default Pagination;

